import { useEffect } from 'react';

import { DrawerCloseOptions, notify } from '@cast/design-system';
import { ListSlackConfigurationsResponse, Webhook } from '@cast/types';

import { AddAlertDrawer } from '../AddAlertDrawer';
import { AlertFormSubdrawer } from '../form';

type UseSlackOAuthReturnParams = {
  open?: boolean;
  searchParams: URLSearchParams;
  webhooks?: (Webhook | undefined)[];
  slackConfigurations: ListSlackConfigurationsResponse | undefined;
  close: (options?: DrawerCloseOptions) => void;
  openDrawer: (config: {
    component: React.ComponentType<any>;
    props: any;
  }) => void;
};

export const useSlackOAuthReturn = ({
  open,
  searchParams,
  webhooks,
  slackConfigurations,
  close,
  openDrawer,
}: UseSlackOAuthReturnParams) => {
  useEffect(() => {
    if (!open) return;

    const stateParam = searchParams.get('state');
    const codeParam = searchParams.get('code');

    // Only process if both state AND code params present (valid OAuth return)
    // This prevents re-processing stale state after OAuth completion
    if (!stateParam || !codeParam) {
      return;
    }

    // Handle return from Slack OAuth
    if (stateParam) {
      try {
        const state = JSON.parse(stateParam);

        // Handle create mode
        if (state.formState && state.mode === 'create') {
          close({
            onComplete: () => {
              openDrawer({
                component: AddAlertDrawer,
                props: {},
              });
            },
          });
          return;
        }

        // Handle edit mode
        if (state.formState && state.mode === 'edit' && state.alertId) {
          // Find the alert from the data
          const alerts = [
            ...(slackConfigurations?.items ?? []),
            ...(webhooks ?? []),
          ];
          const alert = alerts.find((a) => a?.id === state.alertId);

          if (alert) {
            close({
              onComplete: () => {
                const props =
                  'channels' in alert
                    ? { slackConfiguration: alert }
                    : { webhook: alert };
                openDrawer({
                  component: AlertFormSubdrawer,
                  props,
                });
              },
            });
          }
          return;
        }
      } catch (error) {
        notify.error('Failed to restore form state');
        console.error('Failed to parse Slack OAuth state:', error);
      }
    }
  }, [open, searchParams, close, openDrawer, webhooks, slackConfigurations]);
};
